import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Flex,
  Spinner,
  Badge,
  useToast,
} from "@chakra-ui/react";
import { MdGraphicEq } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AudioRecorder from "./AudioRecorder";
import { createUpdateSignature, getUserSignature } from "../services/backend";
import PageLayoutWithHeaderNav from "./PageLayoutWithHeaderNav";
import { SIGNATURE_PROMPT_TEXT } from "../constants/signaturePrompt";

/**
 * Voice signature page (/edit-signature).
 *
 * Delegates all recording/playback to the shared <AudioRecorder>, which owns
 * mic selection, file upload, the existing-signature preview (when objectId is
 * passed), and the Safari/WebKit audio-session handling. This page only owns
 * the signature metadata flow: load the user's signature, then on upload
 * complete create-or-update the Signature row.
 *
 * History: the original bespoke RecordingWidget/WaveformPreview here duplicated
 * AudioRecorder's WaveSurfer logic and missed the audio-session fix, so Safari
 * recording broke on HTTPS. The earlier EditSignatureView already used
 * AudioRecorder but never persisted the row (createUpdateSignature) — this keeps
 * the shared recorder AND the persistence.
 */
const EditSignatureObjectView = ({ onSignatureUpdated }) => {
  const [existingSignature, setExistingSignature] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const signature = await getUserSignature();
        if (signature) setExistingSignature(signature);
      } catch {
        toast({
          title: "Error",
          description: "Failed to load signature.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsInitialLoading(false);
      }
    };
    load();
  }, [toast]);

  // Called by AudioRecorder after the audio blob is uploaded to S3. Persist the
  // Signature row (create for a new user, update to replace an existing one).
  const handleUploadComplete = async (uuid, filePath, bucketName) => {
    try {
      const isUpdate = !!existingSignature;
      const data = {
        uuid,
        description: existingSignature?.description || "",
        audio_url: `s3://${bucketName}/${filePath}`,
        timestamp: new Date().toISOString(),
      };
      if (existingSignature) data.id = existingSignature.id;

      const updated = await createUpdateSignature(data, isUpdate);
      setExistingSignature(updated);
      if (onSignatureUpdated) onSignatureUpdated(updated);

      toast({
        title: isUpdate ? "Signature updated." : "Signature created.",
        description: "Your voice signature has been saved.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setTimeout(() => navigate("/audio-batches"), 600);
    } catch {
      toast({
        title: "Error",
        description: "Failed to save signature.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (isInitialLoading) {
    return (
      <PageLayoutWithHeaderNav>
        <Flex justify="center" align="center" minH="50vh" direction="column" gap={3}>
          <Spinner size="lg" color="blue.500" thickness="3px" />
          <Text fontSize="sm" color="gray.400">Loading your signature…</Text>
        </Flex>
      </PageLayoutWithHeaderNav>
    );
  }

  return (
    <PageLayoutWithHeaderNav>
      <Box maxW="520px" mx="auto">
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box>
            <HStack spacing={3} mb={1}>
              <Box
                w={8}
                h={8}
                borderRadius="lg"
                bg="blue.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <MdGraphicEq size={18} color="var(--chakra-colors-blue-500)" />
              </Box>
              <Heading size="md" letterSpacing="-0.3px">Voice Signature</Heading>
              {existingSignature && (
                <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2} fontSize="2xs">
                  Active
                </Badge>
              )}
            </HStack>
            <Text fontSize="sm" color="gray.400" pl={11}>
              {existingSignature
                ? "Record or upload a new clip to replace your signature"
                : "A short recording used to identify you in conversations"}
            </Text>
          </Box>

          {/* Recorder (shared component owns record/upload/preview/mic-select) */}
          <Box
            bg="white"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="gray.200"
            boxShadow="sm"
            px={5}
            py={5}
          >
            <VStack spacing={5} align="stretch">
              {/* Signature prompt — script for the user to read aloud */}
              <Box
                bg="gray.50"
                borderRadius="lg"
                borderWidth="1px"
                borderColor="gray.200"
                px={4}
                py={3}
              >
                <Text
                  fontSize="xs"
                  color="gray.500"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={2}
                >
                  Signature prompt
                </Text>
                <Text fontSize="sm" color="gray.700" whiteSpace="pre-line">
                  {SIGNATURE_PROMPT_TEXT}
                </Text>
              </Box>

              <AudioRecorder
                uploadType="signature"
                objectId={existingSignature?.uuid || null}
                onUploadComplete={handleUploadComplete}
                disableCreateButton={false}
              />
            </VStack>
          </Box>
        </VStack>
      </Box>
    </PageLayoutWithHeaderNav>
  );
};

export default EditSignatureObjectView;
